import React from 'react'
import PropTypes from 'prop-types'
import useLockBodyScroll from 'react-use/lib/useLockBodyScroll'
import { PageTakeover } from '../page-takeover/PageTakeover'
import { ZoomInAction, ZoomOutAction } from '../buttons'
import {
  combineClasses,
  useDropdownAria,
  useFocusTrap,
  useKeyWhenActive,
} from '~/util'
import { Image } from '../image'
import { ANALYTICS_CATEGORIES } from '~/service'
import styles from './ExpandableHeaderImage.module.scss'

export function ExpandableHeaderImageContent({
  contentContainerClassName,
  imageClassName,
  src,
  alt,
  expanded = false,
  minHeight,
  preExpanded,
  disabled,
  onToggleExpand,
  onLoad,
  onError,
  elementRef,
  children,
  ...rest
}) {
  const aria = useDropdownAria(expanded)
  // When the map is fullscreen, it should act as a focus trap
  // so that elements behind the map are not reachable with
  // the keyboard.
  const [lastFocusableRef] = useFocusTrap(expanded)

  useKeyWhenActive('Escape', onToggleExpand, expanded)
  return (
    <div
      className={combineClasses(
        styles.ExpandableMapContent,
        contentContainerClassName
      )}
      ref={elementRef}
      style={{
        minHeight: minHeight,
      }}
      {...rest}
    >
      <Image
        data-testid="floorPlanImage"
        className={imageClassName}
        src={src}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
        display="block"
        fit="contain"
        width="100%"
        height="100%"
        responsive
      />
      {preExpanded && !disabled && (
        // Not tracked: every zoom in is eventually closed, so tracking the
        // close only duplicates the zoom in count.
        <ZoomOutAction
          data-testid="zoomOutButton"
          aria-label="Zoom Out"
          className={styles.zoomButton}
          onClick={onToggleExpand}
          button
          size="m"
          transparent
          {...aria.triggerProps}
          ref={lastFocusableRef}
        />
      )}
      {!preExpanded && !disabled && (
        <ZoomInAction
          data-testid="zoomInButton"
          aria-label="Zoom In"
          className={styles.zoomButton}
          onClick={onToggleExpand}
          button
          size="m"
          transparent
          category={ANALYTICS_CATEGORIES.MEDIA}
          action="Zoom"
          label="Floor Plan"
          {...aria.triggerProps}
          ref={lastFocusableRef}
        />
      )}
    </div>
  )
}

/**
 * `<ExpandableHeaderImage>`
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {string} [props.contentContainerClassName]
 * @param {string} [props.imageClassName]
 * @param {string} [props.src]
 * @param {string} [props.alt]
 * @param {boolean} [props.allowBodyScroll]
 * @param {boolean} [props.preExpanded]
 * @param {boolean} [props.disabled]
 * @param {function} [props.onLoad]
 * @param {function} [props.onMaximize]
 * @param {function} [props.onMinimize]
 * @param {string} [props.onToggleExpand]
 */
export function ExpandableHeaderImage({
  contentContainerClassName,
  imageClassName,
  className,
  src,
  alt,
  allowBodyScroll,
  onLoad,
  onError,
  disabled,
  onMaximize: onMaximizeProp,
  onMinimize: onMinimizeProp,
  ...rest
}) {
  const [expanded, setExpanded] = React.useState(false)

  const onMinimize = (e) => {
    onMinimizeProp?.(e)
    setExpanded(false)
  }
  const onMaximize = (e) => {
    onMaximizeProp?.(e)
    setExpanded(true)
  }

  // Prevent page scrolling while expanded.
  useLockBodyScroll(expanded && !allowBodyScroll)
  return (
    <PageTakeover
      data-testid="ExpandableHeaderImage"
      className={combineClasses(styles.PageTakeover, className)}
      onMaximize={onMaximize}
      onMinimize={onMinimize}
    >
      <ExpandableHeaderImageContent
        contentContainerClassName={contentContainerClassName}
        imageClassName={imageClassName}
        src={src}
        alt={alt}
        disabled={disabled}
        onLoad={onLoad}
        onError={onError}
        {...rest}
      />
    </PageTakeover>
  )
}

ExpandableHeaderImage.propTypes = {
  contentContainerClassNames: PropTypes.string,
  imageClassNames: PropTypes.string,
  src: PropTypes.string,
  alt: PropTypes.string,
  allowBodyScroll: PropTypes.bool,
  preExpanded: PropTypes.bool,
  onLoad: PropTypes.func,
  onToggleExpand: PropTypes.string,
}
